import React, { FC } from "react";

import { useTranslation } from "react-i18next";

import { StatusColorType } from "@/components/atoms/Status/styled";
import { InsuredGoodRootCategory } from "@/components/features/InsuredGoods/utils/InsuredGoodRootCategory";
import { Card, InsurerLinkDropdown } from "@/components/molecules";
import { CardInfo } from "@/components/molecules/Cards/Card/components/CardInfo.interface";
import { BrokerState, Claim, Permissions } from "@/shared/domain";
import { useAuth, useNavigation } from "@/shared/hooks";
import { getInsurerLinks } from "@/shared/utils";

export type ClaimItemProps = {
    claim: Claim | any;
    simplified?: boolean;
};

export const ClaimItem: FC<ClaimItemProps> = ({ claim, simplified = false }) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const { navToClaimDetail } = useNavigation();
    let InsuredGoodCategoryLogo;

    if (!simplified) {
        InsuredGoodCategoryLogo = InsuredGoodRootCategory.getFromId(claim.category.id).categoryIcon;
    }
    const canViewDetails = user.hasPermission(Permissions.ViewClaimsDetails, claim.policyholderId);

    const handleClaimDetails = () => {
        return canViewDetails && navToClaimDetail(claim);
    };
    const insurerFsma = claim.insurer?.fsma ? `(${claim.insurer?.fsma})` : "";

    const infos: Array<CardInfo | React.ReactElement> = [
        {
            title: claim.objectType?.name,
            content: claim.objectDescription,
        },
    ];

    infos.pushIfDefined(claim.victim && { title: t("Common.Victim"), content: claim.victim });

    const insurerLinks = getInsurerLinks(claim);
    infos.pushIf(
        insurerLinks.length,
        <InsurerLinkDropdown
            insurerLinks={insurerLinks}
            id={claim.id}
            insurer={claim.insurer}
            key={`insurer_link-${claim.id}`}
        />,
    );

    return (
        <Card
            onClickCard={handleClaimDetails}
            dataTestId={`claim_item-card-${claim.id}`}
            clickable={canViewDetails}
            owner={!simplified && claim.policyHolder}
            statusColor={claim.brokerState.id === BrokerState.ON_GOING ? StatusColorType.Info : StatusColorType.Success}
            statusText={claim.brokerState.name}
            companyName={`${claim.insurer.name}${insurerFsma}`}
            companyIcon={claim.insurer.logoUrl}
            date={claim.damageEventDate}
            title={claim.description}
            reference={`Ref ${claim.reference}`}
            text={claim.circumstance?.name}
            infos={infos}
            categoryIcon={!simplified && InsuredGoodCategoryLogo}
        />
    );
};

export default ClaimItem;
